import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/vehicle-icons/website-logo-3.png";
import loginBg from "../assets/vehicle-icons/login-bg.png";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  DirectionsCar,
  Shield,
  FlashOn,
  ArrowForward,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup.object({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup.string().min(6, "Min 6 chars").required("Password is required"),
});

interface LoginFormData {
  email: string;
  password: string;
}

const loginFieldSx = {
  mb: 1.8,
  "& .MuiInputLabel-root": {
    color: "#d8e0ea",
    fontWeight: 600,
    transform: "translate(0, -8px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(0, -8px) scale(1)",
  },
  "& .MuiOutlinedInput-root": {
    mt: 1.8,
    height: 52,
    backgroundColor: "rgba(4, 11, 20, 0.68)",
    color: "#ffffff",
    borderRadius: 1.4,
    transition: "0.2s ease",
    "& fieldset": { borderColor: "rgba(153, 174, 201, 0.22)" },
    "&:hover fieldset": { borderColor: "rgba(55, 238, 120, 0.72)" },
    "&.Mui-focused fieldset": {
      borderColor: "#37ee78",
      boxShadow: "0 0 0 3px rgba(55, 238, 120, 0.12)",
    },
  },
  "& .MuiFormHelperText-root": { color: "#ff9aa2", ml: 0 },
  "& input::placeholder": { color: "#8f9aad", opacity: 1 },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 30px #07111d inset !important",
    WebkitTextFillColor: "#ffffff !important",
    caretColor: "#ffffff",
  },
};

const featureItems = [
  { icon: <DirectionsCar />, label: "Smart Fleet", sub: "Management" },
  { icon: <FlashOn />, label: "Real-time", sub: "Monitoring" },
  { icon: <Shield />, label: "Secure &", sub: "Reliable" },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema) as any,
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError("");
      await login(data);
      navigate("/");
    } catch (err: any) {
      let errorMessage = "Failed to log in";

      if (err.status === 401 || err.response?.status === 401) {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (err.message?.includes("401")) {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (err.status === 400 || err.response?.status === 400) {
        errorMessage =
          err.response?.data?.message || "Invalid input. Please check your credentials.";
      } else if (err.status === 500 || err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.message?.includes("Network")) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = err.message || "Failed to log in";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1.08fr 0.92fr" },
        background:
          "radial-gradient(circle at 50% 45%, rgba(32, 224, 105, 0.18), transparent 24%), linear-gradient(135deg, #020a12 0%, #06131d 52%, #020810 100%)",
        fontFamily: "'Poppins', sans-serif",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 360, lg: "100dvh" },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: { md: 4, xl: 5 },
          color: "white",
          borderTopRightRadius: { md: 0, lg: 34 },
          borderBottomRightRadius: { md: 0, lg: 34 },
          borderRight: "1px solid rgba(72, 237, 133, 0.34)",
          backgroundImage: `linear-gradient(90deg, rgba(1, 8, 14, 0.78) 0%, rgba(1, 8, 14, 0.42) 52%, rgba(1, 8, 14, 0.14) 100%), url(${loginBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            component="img"
            src={logo}
            alt="EV91 Logo"
            sx={{
              width: { md: 170, xl: 210 },
              maxHeight: 82,
              objectFit: "contain",
              filter: "drop-shadow(0 10px 26px rgba(33, 234, 120, 0.28))",
            }}
          />
        </Box>

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: 650, pb: { md: 1, xl: 2 } }}>
          <Typography
            variant="h1"
            sx={{
              m: 0,
              fontWeight: 900,
              lineHeight: 1.08,
              fontSize: { md: "2.9rem", xl: "3.7rem" },
              color: "#ffffff",
              textShadow: "0 14px 40px rgba(0, 0, 0, 0.72)",
            }}
          >
            Drive The Future
            <Box component="span" sx={{ display: "block" }}>
              With{" "}
              <Box component="span" sx={{ color: "#33e977" }}>
                Voice <FlashOn sx={{ fontSize: "0.86em", mb: "-0.06em" }} />
              </Box>
            </Box>
          </Typography>

          <Typography
            sx={{
              mt: 2,
              color: "#d6e0ed",
              fontSize: { md: "1rem", xl: "1.08rem" },
              lineHeight: 1.55,
              maxWidth: 540,
            }}
          >
            Welcome to the EV91 Technologies Dashboard. Manage vehicles, riders
            and operations with ease.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 2,
              maxWidth: 570,
            }}
          >
            {featureItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "48px 1fr",
                  gap: 1.3,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    color: "#35ed78",
                    background: "rgba(35, 237, 120, 0.13)",
                    border: "1px solid rgba(35, 237, 120, 0.68)",
                    boxShadow: "0 0 22px rgba(35, 237, 120, 0.16)",
                    "& svg": { fontSize: 25 },
                  }}
                >
                  {item.icon}
                </Box>
                <Typography sx={{ color: "#ffffff", fontWeight: 700, lineHeight: 1.25 }}>
                  {item.label}
                  <Box component="span" sx={{ display: "block", color: "#d8e0ea", fontWeight: 500 }}>
                    {item.sub}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, sm: 3, lg: 4 },
          position: "relative",
          background:
            "radial-gradient(circle at 50% 42%, rgba(33, 234, 120, 0.15), transparent 26%), linear-gradient(135deg, rgba(3, 12, 20, 0.7), rgba(1, 7, 13, 0.38))",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="EV91 Logo"
          sx={{
            display: { xs: "block", md: "none" },
            width: 160,
            maxHeight: 76,
            objectFit: "contain",
            mb: 3,
          }}
        />

        <Card
          sx={{
            width: "100%",
            maxWidth: 520,
            borderRadius: 3,
            py: { xs: 3, sm: 4 },
            px: { xs: 2.2, sm: 4.2, lg: 4.8 },
            background:
              "linear-gradient(145deg, rgba(16, 28, 42, 0.78), rgba(7, 16, 29, 0.88))",
            backdropFilter: "blur(22px)",
            border: "1px solid rgba(183, 224, 224, 0.56)",
            boxShadow:
              "0 28px 90px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: "0 0 auto auto",
              width: 190,
              height: 130,
              borderBottomLeftRadius: 100,
              background:
                "repeating-radial-gradient(ellipse at top right, rgba(113, 243, 173, 0.24) 0 1px, transparent 1px 11px)",
              opacity: 0.45,
            },
          }}
        >
          <CardContent sx={{ p: 0, position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: "#ffffff",
                textAlign: "center",
                lineHeight: 1.15,
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
              }}
            >
              Welcome{" "}
              <Box component="span" sx={{ color: "#36ea78" }}>
                Back
              </Box>
            </Typography>

            <Box
              sx={{
                width: 54,
                height: 3,
                mx: "auto",
                mt: 1.2,
                mb: 1.7,
                borderRadius: 10,
                background: "#35ed78",
              }}
            />

            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mb: 2.4,
                color: "#cdd6e3",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
              }}
            >
              Log in to continue to your EV91 dashboard
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 1.8,
                  backgroundColor: "rgba(244, 67, 54, 0.14)",
                  borderLeft: "4px solid #ff5f68",
                  color: "#ffd6da",
                  fontWeight: 500,
                  fontSize: "0.95rem",
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#ff7079",
                  },
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter your email"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "#37ee78" }} />
                    </InputAdornment>
                  ),
                }}
                {...register("email")}
                sx={loginFieldSx}
              />

              <TextField
                fullWidth
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: "#37ee78" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ color: "#c9d2df" }} />
                        ) : (
                          <Visibility sx={{ color: "#c9d2df" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register("password")}
                sx={{ ...loginFieldSx, mb: 1.5 }}
              />

              <Box
                sx={{
                  mt: 0.5,
                  mb: 2.3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                      sx={{
                        color: "#37ee78",
                        p: 0.5,
                        "&.Mui-checked": { color: "#37ee78" },
                      }}
                    />
                  }
                  label="Remember me"
                  sx={{
                    m: 0,
                    color: "#d5deea",
                    "& .MuiFormControlLabel-label": { fontSize: "0.96rem" },
                  }}
                />
                <Link
                  to="/forgot-password"
                  component={RouterLink}
                  sx={{
                    color: "#37ee78",
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                variant="contained"
                endIcon={!loading ? <ArrowForward /> : undefined}
                sx={{
                  py: 1.75,
                  minHeight: 54,
                  borderRadius: 1.4,
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  background: "linear-gradient(90deg, #1687ff 0%, #27df6e 100%)",
                  color: "#ffffff",
                  boxShadow: "0 18px 36px rgba(24, 135, 255, 0.26)",
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 22px 42px rgba(35, 223, 110, 0.26)",
                    filter: "brightness(1.04)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#ffffff" }} /> : "Sign In"}
              </Button>

              <Divider
                sx={{
                  my: 2.6,
                  color: "#9aa7b8",
                  "&:before, &:after": { borderColor: "rgba(154, 167, 184, 0.22)" },
                }}
              >
                OR
              </Divider>

              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#d5deea", fontSize: "0.98rem" }}
              >
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  component={RouterLink}
                  sx={{
                    fontWeight: 700,
                    color: "#37ee78",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {/* Sign up */}
                </Link>
              </Typography>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
